import { trackAddToCart } from "./analytics";

export interface CartItem {
  id: string;
  artworkId?: string;
  artSupplyId?: string;
  title: string;
  price: number;
  quantity: number;
  imageUrl: string;
  artistId?: string;
  artistName?: string;
  type: "artwork" | "supply";
  artworkType?: "original" | "print" | "printful";
  salesModel?: "direct_sale" | "mediation" | "license"; // Sales model from artworks table
  variantId?: string; // For art supplies or artwork variants
  variantName?: string; // Display name for selected variant
  selectedVariantId?: string; // For artwork_variants (Printful sizes)
}

const CART_STORAGE_KEY = "nac_cart";

export const getCart = (): CartItem[] => {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

export const saveCart = (items: CartItem[]): void => {
  localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
};

export const addToCart = (item: Omit<CartItem, "id">): CartItem => {
  const cart = getCart();
  const existingIndex = cart.findIndex(
    (i) =>
      (item.artworkId && i.artworkId === item.artworkId && i.selectedVariantId === item.selectedVariantId) ||
      (item.artSupplyId && i.artSupplyId === item.artSupplyId && i.variantId === item.variantId)
  );

  if (existingIndex >= 0) {
    cart[existingIndex].quantity += item.quantity;
  } else {
    const newItem = { ...item, id: crypto.randomUUID() };
    cart.push(newItem);
  }

  saveCart(cart);
  
  // Track add to cart event
  trackAddToCart(
    item.artworkId || item.artSupplyId || "",
    item.title,
    item.price,
    item.quantity,
    item.type === "artwork" ? item.artworkType : "supply",
    item.artistName
  );
  
  // Dispatch event to open cart sheet
  window.dispatchEvent(new Event("itemAddedToCart"));
  
  return cart[existingIndex >= 0 ? existingIndex : cart.length - 1];
};

export const removeFromCart = (itemId: string): void => {
  const cart = getCart().filter((item) => item.id !== itemId);
  saveCart(cart);
};

export const updateCartItemQuantity = (itemId: string, quantity: number): void => {
  const cart = getCart();
  const item = cart.find((i) => i.id === itemId);
  if (item) {
    item.quantity = Math.max(1, quantity);
    saveCart(cart);
  }
};

export const clearCart = (): void => {
  localStorage.removeItem(CART_STORAGE_KEY);
};

export const getCartTotal = (): number => {
  return getCart().reduce((sum, item) => sum + item.price * item.quantity, 0);
};

export const getCartCount = (): number => {
  return getCart().reduce((sum, item) => sum + item.quantity, 0);
};
